<script setup>
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
  formatAuditLogTime,
} from 'dashboard/helper/auditlogHelper';
import {
  computed,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);
const currentAccountId = computed(() => getters.getCurrentAccountId.value);

const { t, locale } = useI18n();
const route = useRoute();

const routerPage = computed(() => Number(route.query.page ?? 1));

// Filtros na URL (pessoa, tipo e periodo): recarregar ou mandar o link mantem o
// recorte. Tipo vazio = tudo menos entrada e saida, que eram 63% da lista e
// enterravam mudanca de caixa, conta e membros.
const FILTER_KEYS = ['user_id', 'type', 'from', 'to'];
const filters = computed(() =>
  FILTER_KEYS.reduce((result, key) => {
    const value = route.query[key];
    if (value) result[key] = String(value);
    return result;
  }, {})
);
const filtersKey = computed(() => JSON.stringify(filters.value));
const hasFilters = computed(() => Object.keys(filters.value).length > 0);

const personOptions = computed(() => [
  { value: '', label: t('AUDIT_LOGS.FILTERS.ALL_PEOPLE') },
  ...(agentList.value || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  })),
]);
const typeOptions = computed(() =>
  ['', 'inbox', 'account', 'members', 'access', 'other', 'all'].map(value => ({
    value,
    label: t(`AUDIT_LOGS.FILTERS.TYPES.${(value || 'default').toUpperCase()}`),
  }))
);
let fetchSequence = 0;
const isActive = ref(false);
let wasDeactivated = false;

const fetchAuditLogs = async page => {
  fetchSequence += 1;
  const sequence = fetchSequence;
  const accountId = currentAccountId.value;
  try {
    await store.dispatch('auditlogs/fetch', { page, filters: filters.value });
  } catch (error) {
    if (sequence === fetchSequence && accountId === currentAccountId.value) {
      useAlert(error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE'));
    }
  }
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(
    auditLogItem,
    agentList.value,
    t('AUDIT_LOGS.DEFAULT_USER')
  );
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  // type e action alimentam a frase generica (AUDIT_LOGS.GENERIC) de acao sem
  // chave propria, que antes virava uma linha em branco.
  const mergedPayload = {
    type: auditLogItem.auditable_type,
    action: auditLogItem.action,
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const setFilter = (key, value) => {
  const query = { ...route.query, page: '1' };
  if (value) query[key] = String(value);
  else delete query[key];
  router.push({ name: 'auditlogs_list', query });
};

const clearFilters = () => {
  const query = { ...route.query, page: '1' };
  FILTER_KEYS.forEach(key => delete query[key]);
  router.push({ name: 'auditlogs_list', query });
};

const onPageChange = page => {
  router.push({
    name: 'auditlogs_list',
    query: { ...route.query, page: String(page) },
  });
};

const retryFetch = () => fetchAuditLogs(routerPage.value);

onMounted(() => {
  isActive.value = true;
  store.dispatch('agents/get');
  fetchAuditLogs(routerPage.value);
});

onActivated(() => {
  isActive.value = true;
  if (wasDeactivated) {
    wasDeactivated = false;
    fetchAuditLogs(routerPage.value);
  }
});

onDeactivated(() => {
  wasDeactivated = true;
  isActive.value = false;
  fetchSequence += 1;
});

onUnmounted(() => {
  fetchSequence += 1;
});

watch(
  [routerPage, currentAccountId, filtersKey],
  ([newPage, newAccount, newFilters], [oldPage, oldAccount, oldFilters]) => {
    if (
      isActive.value &&
      (newPage !== oldPage ||
        newAccount !== oldAccount ||
        newFilters !== oldFilters)
    ) {
      fetchAuditLogs(newPage);
    }
  }
);

const tableHeaders = computed(() => {
  return [
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
  ];
});
</script>

<template>
  <SettingsLayout
    data-testid="page-auditlogs"
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('AUDIT_LOGS.LOADING')"
    :no-records-found="!records.length && !uiFlags.fetchError"
    :no-records-message="$t('AUDIT_LOGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUDIT_LOGS.HEADER')"
        :description="$t('AUDIT_LOGS.DESCRIPTION')"
        :link-text="$t('AUDIT_LOGS.LEARN_MORE')"
        feature-name="audit_logs"
      />
      <!-- Os filtros ficam no cabecalho: com a lista vazia o corpo vira o
           estado vazio, e o filtro que esvaziou precisa continuar a mao. -->
      <div
        class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="auditlogs-filters"
      >
        <div class="flex flex-col gap-1">
          <span
            id="auditlogs-filter-person"
            class="text-xs font-medium text-n-slate-11"
          >
            {{ t('AUDIT_LOGS.FILTERS.PERSON') }}
          </span>
          <Select
            :model-value="filters.user_id || ''"
            :options="personOptions"
            labelledby="auditlogs-filter-person"
            class="[&_select]:w-full [&_select]:min-h-11 !w-full"
            data-testid="auditlogs-filter-person"
            @update:model-value="value => setFilter('user_id', value)"
          />
        </div>
        <div class="flex flex-col gap-1">
          <span
            id="auditlogs-filter-type"
            class="text-xs font-medium text-n-slate-11"
          >
            {{ t('AUDIT_LOGS.FILTERS.TYPE') }}
          </span>
          <Select
            :model-value="filters.type || ''"
            :options="typeOptions"
            labelledby="auditlogs-filter-type"
            class="[&_select]:w-full [&_select]:min-h-11 !w-full"
            data-testid="auditlogs-filter-type"
            @update:model-value="value => setFilter('type', value)"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label
            for="auditlogs-filter-from"
            class="text-xs font-medium text-n-slate-11"
          >
            {{ t('AUDIT_LOGS.FILTERS.FROM') }}
          </label>
          <Input
            id="auditlogs-filter-from"
            :model-value="filters.from || ''"
            type="date"
            :max="filters.to || ''"
            custom-input-class="min-h-11"
            data-testid="auditlogs-filter-from"
            @update:model-value="value => setFilter('from', value)"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label
            for="auditlogs-filter-to"
            class="text-xs font-medium text-n-slate-11"
          >
            {{ t('AUDIT_LOGS.FILTERS.TO') }}
          </label>
          <Input
            id="auditlogs-filter-to"
            :model-value="filters.to || ''"
            type="date"
            :min="filters.from || ''"
            custom-input-class="min-h-11"
            data-testid="auditlogs-filter-to"
            @update:model-value="value => setFilter('to', value)"
          />
        </div>
      </div>
      <div class="mt-2 flex flex-wrap items-center gap-3">
        <p class="m-0 text-xs text-n-slate-11">
          {{
            filters.type === 'access' || filters.type === 'all'
              ? t('AUDIT_LOGS.FILTERS.ACCESS_SHOWN')
              : t('AUDIT_LOGS.FILTERS.ACCESS_HIDDEN')
          }}
        </p>
        <Button
          v-if="hasFilters"
          variant="link"
          color="slate"
          size="sm"
          class="min-h-11"
          :label="t('AUDIT_LOGS.FILTERS.CLEAR')"
          data-testid="auditlogs-filter-clear"
          @click="clearFilters"
        />
      </div>
    </template>
    <template #body>
      <div
        v-if="uiFlags.fetchError"
        role="alert"
        class="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-ruby-7 bg-n-ruby-1 px-3 py-2 text-sm text-n-ruby-11"
      >
        <span>{{ $t('AUDIT_LOGS.API.ERROR_MESSAGE') }}</span>
        <Button
          variant="faded"
          color="ruby"
          :label="$t('AUDIT_LOGS.API.RETRY')"
          @click="retryFetch"
        />
      </div>
      <div class="flex flex-col">
        <BaseTable :headers="tableHeaders" :items="records">
          <template #row="{ items }">
            <BaseTableRow
              v-for="auditLogItem in items"
              :key="auditLogItem.id"
              :item="auditLogItem"
            >
              <template #default>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-12 break-words">
                    {{ generateLogText(auditLogItem) }}
                  </span>
                </BaseTableCell>

                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-11 whitespace-nowrap"
                  >
                    {{
                      formatAuditLogTime(auditLogItem.created_at, locale?.value)
                    }}
                  </span>
                </BaseTableCell>

                <BaseTableCell class="w-36">
                  <span class="text-body-main text-n-slate-11">
                    {{ auditLogItem.remote_address }}
                  </span>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
        <PaginationFooter
          :current-page="Number(meta.currentPage)"
          :total-items="meta.totalEntries"
          :items-per-page="meta.perPage"
          class="!px-0"
          @update:current-page="onPageChange"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
